/**
 * IdentificationStep Component
 * Step 1: Basic identification fields for EPI
 *
 * @version 2025.1.0
 */

import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Typography,
  Box,
} from '@mui/material';
import { EpiFormData, TipoDeRisco, EpiStatus } from '../../../../../types/epi';
import { IdentificationStepProps } from '../EPIFormWizard.types';

export const IdentificationStep: React.FC<IdentificationStepProps> = ({
  disabled = false,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<EpiFormData>();

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Informe os dados básicos de identificação do EPI (Equipamento de Proteção Individual).
      </Typography>

      <Grid container spacing={3}>
        {/* Nome */}
        <Grid
          size={{
            xs: 12,
            md: 8,
          }}
        >
          <Controller
            name="nome"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Nome do EPI"
                fullWidth
                required
                error={Boolean(errors.nome)}
                helperText={errors.nome?.message || 'Ex: Protetor Auricular tipo Plug'}
                disabled={disabled}
              />
            )}
          />
        </Grid>

        {/* Status */}
        <Grid
          size={{
            xs: 12,
            md: 4,
          }}
        >
          <FormControl fullWidth required>
            <InputLabel>Status</InputLabel>
            <Controller
              name="ativo"
              control={control}
              render={({ field }) => (
                <Select {...field} label="Status" disabled={disabled}>
                  <MenuItem value={EpiStatus.ATIVO}>Ativo</MenuItem>
                  <MenuItem value={EpiStatus.INATIVO}>Inativo</MenuItem>
                </Select>
              )}
            />
          </FormControl>
        </Grid>

        {/* Tipo de Risco */}
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <FormControl fullWidth required error={Boolean(errors.tipoDeRisco)}>
            <InputLabel>Tipo de Risco</InputLabel>
            <Controller
              name="tipoDeRisco"
              control={control}
              render={({ field }) => (
                <Select {...field} label="Tipo de Risco" disabled={disabled}>
                  <MenuItem value={TipoDeRisco.FISICO}>Físico</MenuItem>
                  <MenuItem value={TipoDeRisco.QUIMICO}>Químico</MenuItem>
                  <MenuItem value={TipoDeRisco.BIOLOGICO}>Biológico</MenuItem>
                  <MenuItem value={TipoDeRisco.ERGONOMICO}>Ergonômico</MenuItem>
                  <MenuItem value={TipoDeRisco.ACIDENTE}>Acidente</MenuItem>
                </Select>
              )}
            />
            {errors.tipoDeRisco && <FormHelperText>{errors.tipoDeRisco.message}</FormHelperText>}
          </FormControl>
        </Grid>

        {/* Risco */}
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <Controller
            name="risco"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Descrição do Risco"
                fullWidth
                required
                error={Boolean(errors.risco)}
                helperText={errors.risco?.message || 'Ex: Ruído contínuo acima de 85 dB(A)'}
                disabled={disabled}
              />
            )}
          />
        </Grid>

        {/* Orientação de uso */}
        <Grid size={{ xs: 12 }}>
          <Controller
            name="orientacaoDeUso"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Orientação de uso"
                fullWidth
                multiline
                rows={4}
                error={Boolean(errors.orientacaoDeUso)}
                helperText={
                  errors.orientacaoDeUso?.message ||
                  'Como usar, higienizar e guardar o EPI. Aparece no anexo de EPIs do PGR.'
                }
                disabled={disabled}
              />
            )}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default IdentificationStep;
